import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  ShieldCheck,
  Search,
  DollarSign,
  AlertTriangle,
  Tags,
  Layers,
  ClipboardCheck,
  Award,
  Landmark,
  Settings,
  ShieldAlert,
  Scale,
} from 'lucide-react';
import { ZijMenu, ZijMenuMobielKnop, PaginaKop, type ZijMenuGroep } from './ZijMenu';

/** Linkerkolom voor alle /admin-pagina's (BeLagom-huisstijl okt 2026). */
const GROEPEN: Array<{ label: string; items: Array<{ key: string; label: string; href: string; icon: React.ComponentType<{ className?: string }> }> }> = [
  {
    label: 'Platform',
    items: [
      { key: 'dashboard', label: 'Dashboard', href: '/admin', icon: Home },
      { key: 'users', label: 'Gebruikers', href: '/admin?view=users', icon: Users },
      { key: 'screenings', label: 'Screenings', href: '/admin/screenings', icon: ShieldCheck },
      { key: 'search', label: 'Zoekopdrachten', href: '/admin/search-requests', icon: Search },
      { key: 'klachten', label: 'Platformklachten', href: '/admin/klachten', icon: Scale },
    ],
  },
  {
    label: 'Financieel',
    items: [
      { key: 'invoices', label: 'Facturen', href: '/admin/invoices', icon: DollarSign },
      { key: 'stripe', label: 'Stripe-incidenten', href: '/admin/stripe-incidenten', icon: AlertTriangle },
      { key: 'belastingdienst', label: 'Belastingdienst', href: '/admin/belastingdienst', icon: Landmark },
    ],
  },
  {
    label: 'Kwaliteit & Academy',
    items: [
      { key: 'vetting', label: 'Continuous Vetting', href: '/admin/compliance', icon: ShieldAlert },
      { key: 'paspoorten', label: 'Kwaliteitspaspoorten', href: '/admin/kwaliteitspaspoorten', icon: Award },
      { key: 'sbi', label: 'SBI-codes', href: '/admin/sbi-codes', icon: Tags },
      { key: 'subcat', label: 'Academy-subcategorieën', href: '/admin/academy-subcategorieen', icon: Layers },
      { key: 'audit', label: 'Academy-audit', href: '/admin/academy-audit', icon: ClipboardCheck },
    ],
  },
  {
    label: 'Instellingen',
    items: [{ key: 'profile', label: 'Platforminstellingen', href: '/admin/profile', icon: Settings }],
  },
];

function isActief(href: string, pathname: string, search: string): boolean {
  const [pad, query] = href.split('?');
  if (query) return pathname === pad && search === `?${query}`;
  if (pad === '/admin') return pathname === '/admin' && !new URLSearchParams(search).get('view');
  return pathname === pad || pathname.startsWith(pad + '/');
}

export const AdminSchil: React.FC<{ titel?: string; ondertitel?: string; children: React.ReactNode }> = ({ titel, ondertitel, children }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const groepen: ZijMenuGroep[] = GROEPEN.map((g) => ({
    label: g.label,
    items: g.items.map((i) => ({ ...i, actief: isActief(i.href, location.pathname, location.search) })),
  }));

  return (
    <div className="bg-gray-50">
      <ZijMenuMobielKnop onClick={() => setOpen(true)} />
      <div className="max-w-7xl mx-auto flex min-h-[calc(100vh-4rem)]">
        <ZijMenu
          titel="Beheer"
          ondertitel="Gebruikers, screenings, financiën en kwaliteit"
          groepen={groepen}
          mobielOpen={open}
          onMobielSluit={() => setOpen(false)}
        />
        <div className="flex-1 min-w-0">
          {titel && <PaginaKop titel={titel} ondertitel={ondertitel} className="px-4 sm:px-6 pt-6" />}
          {children}
        </div>
      </div>
    </div>
  );
};
